import React from "react";
import { Paper, Typography, Stack, Box, Tooltip } from "@mui/material";

// Compass dial: arrow points where the wind blows TOWARD.
const WindDial = ({ speed, gusts, direction, size = 92 }) => {
  const towardDeg = ((direction ?? 0) + 180) % 360;
  const c = size / 2;
  const r = c - 6;
  return (
    <svg width={size} height={size}>
      <circle cx={c} cy={c} r={r} fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.15)" />
      {["N", "E", "S", "W"].map((label, i) => {
        const a = (i * 90 * Math.PI) / 180;
        return (
          <text
            key={label}
            x={c + Math.sin(a) * (r - 8)}
            y={c - Math.cos(a) * (r - 8) + 3.5}
            textAnchor="middle"
            fill="rgba(255,255,255,0.4)"
            fontSize="9"
            fontWeight="600"
          >
            {label}
          </text>
        );
      })}
      <g transform={`rotate(${towardDeg} ${c} ${c})`}>
        <path
          d={`M ${c} ${c - r + 14} L ${c - 5} ${c + 6} L ${c} ${c + 1} L ${c + 5} ${c + 6} Z`}
          fill="#ff8a3d"
        />
      </g>
      <text x={c} y={c + 20} textAnchor="middle" fill="#e6edf3" fontSize="13" fontWeight="700">
        {Math.round(speed ?? 0)}
      </text>
      <text x={c} y={c + 31} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize="8">
        mph {gusts && gusts > (speed ?? 0) + 3 ? `· G${Math.round(gusts)}` : ""}
      </text>
    </svg>
  );
};

const Reading = ({ label, value, unit, warn }) => (
  <Box sx={{ minWidth: 64 }}>
    <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.4, fontSize: "0.62rem" }}>
      {label}
    </Typography>
    <Typography variant="h6" sx={{ lineHeight: 1.1, color: warn ? "warning.main" : "text.primary" }}>
      {value}
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.3 }}>
        {unit}
      </Typography>
    </Typography>
  </Box>
);

const ConditionsCard = ({ weather, moisture }) => {
  if (!weather) return null;
  const rh = Math.round(weather.humidity ?? 0);
  const compass = ["N","NNE","NE","ENE","E","ESE","SE","SSE","S","SSW","SW","WSW","W","WNW","NW","NNW"][
    Math.round(((weather.direction ?? 0) % 360) / 22.5) % 16
  ];
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="overline" color="text.secondary">
        Live Conditions
      </Typography>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
        <Tooltip title={`Wind from ${compass} (${Math.round(weather.direction ?? 0)}°) — arrow shows where it blows`}>
          <Box sx={{ flexShrink: 0 }}>
            <WindDial speed={weather.speed} gusts={weather.gusts} direction={weather.direction} />
          </Box>
        </Tooltip>
        <Stack spacing={1} sx={{ flexGrow: 1 }} direction="row" flexWrap="wrap" useFlexGap>
          <Reading label="Humidity" value={rh} unit="%" warn={rh < 25} />
          <Reading label="Temp" value={Math.round(weather.tempC ?? 0)} unit="°C" />
          <Reading
            label="Fuel moisture"
            value={moisture != null ? (moisture * 100).toFixed(1) : "—"}
            unit="%"
            warn={moisture != null && moisture < 0.06}
          />
          <Reading label="Rain 48h" value={(weather.rain48 ?? 0).toFixed(1)} unit="mm" />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default ConditionsCard;
